/**
 * Network quality monitor for the Live Examination UI.
 *
 * - Periodically pings `/api/ping` to measure RTT (round-trip-time in ms).
 * - Samples `navigator.connection` (downlink, effectiveType, rtt) where
 *   available (Chromium / most Android browsers). Unsupported browsers
 *   (Safari, Firefox desktop) fall back to RTT-only classification.
 * - Classifies quality into `good | fair | poor` using conservative
 *   thresholds tuned for shipboard Wi-Fi: we'd rather yellow-flag early
 *   than let a candidate drop silently.
 *
 * Designed as a lightweight singleton so LiveTest and EnvironmentCheck
 * can share a single monitor and agree on the "initial" bitrate to pick.
 */

let BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const CLASSIFY = (pingMs, downlinkMbps) => {
  // If ping is atrocious, quality is poor regardless of downlink.
  if (pingMs == null || pingMs > 900) return "poor";
  if (pingMs > 450) return "fair";
  // Downlink-based refinement when reported by the browser.
  if (downlinkMbps != null) {
    if (downlinkMbps < 0.75) return "poor";
    if (downlinkMbps < 2.0) return "fair";
  }
  return "good";
};

/**
 * Suggest an initial video bitrate (bps) for MediaRecorder based on a
 * one-shot measurement. Conservative defaults — we pick a floor that
 * still captures a readable face + screen.
 */
export function suggestBitrate(quality) {
  switch (quality) {
    case "poor":
      return { video: 350_000, screen: 600_000 }; // ~350 kbps cam, ~600 kbps screen
    case "fair":
      return { video: 650_000, screen: 1_000_000 };
    case "good":
    default:
      return { video: 1_000_000, screen: 1_500_000 };
  }
}

async function pingOnce(timeoutMs = 4000) {
  const url = `${BACKEND_URL}/api/ping?_=${Date.now()}`;
  const started = performance.now();
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { method: "GET", cache: "no-store", signal: ctrl.signal });
    if (!res.ok) return null;
    await res.text();
    return Math.round(performance.now() - started);
  } catch (_e) {
    return null;
  } finally {
    clearTimeout(to);
  }
}

function readNavigatorConnection() {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return {};
  return {
    downlink_mbps: typeof c.downlink === "number" ? c.downlink : null,
    effective_type: c.effectiveType || null,
    navigator_rtt: typeof c.rtt === "number" ? c.rtt : null,
  };
}

export async function measureOnce() {
  const [ping, navConn] = await Promise.all([pingOnce(), Promise.resolve(readNavigatorConnection())]);
  const quality = CLASSIFY(ping, navConn.downlink_mbps);
  return {
    ping_ms: ping,
    downlink_mbps: navConn.downlink_mbps,
    effective_type: navConn.effective_type,
    quality,
    at: new Date().toISOString(),
  };
}

/**
 * Long-running monitor. Invokes `onSample({ping_ms, downlink_mbps, quality, ...})`
 * at `intervalMs` cadence. Returns a stop() function.
 *
 * - Skips sampling when `document.hidden` to avoid polluting the ping
 *   signal with tab-switched RTT.
 * - Emits immediately on start so consumers get a first reading without
 *   waiting the full interval.
 */
export function startMonitor({ intervalMs = 8000, onSample } = {}) {
  if (typeof onSample !== "function") throw new Error("onSample required");
  let cancelled = false;
  let lastSample = null;

  const tick = async () => {
    if (cancelled) return;
    if (!document.hidden) {
      try {
        lastSample = await measureOnce();
        if (!cancelled) onSample(lastSample);
      } catch (_e) { /* swallow — monitor must be resilient */ }
    }
  };

  // Fire immediately, then on interval.
  tick();
  const handle = setInterval(tick, intervalMs);

  return {
    stop() {
      cancelled = true;
      clearInterval(handle);
    },
    last() { return lastSample; },
  };
}
